import { v4 as uuid } from 'uuid';

// the default token source callbacks
// there is no default for the refreshToken callback
const defaultSource = {
    // a unique name is required for each source passed to the constructor.
    // a custom 'default' source can be defined that overwrites these options.
    name: 'default',
    decorateRequest({ requestOptions }) {
        // decorateRequest is passed an object with { token, requestOptions }
        return requestOptions;
    },
    // there is no default refreshToken callback -
    // it would be passed a callback that accepts (error, data) arguments
    // if a custom 'default' source has a refreshToken callback, all other
    // sources will fall-back on that function.
    validTokenForRequest() {
        // validTokenForRequest is passed a token
        return true;
    },
    responseSignalsTokenRefresh() {
        // responseSignalsTokenRefresh is passed a response object from fetch
        return false;
    },
};

// the default minimum/maximum times to sleep before retying a request.
// these can be overwritten by the options object passed to fetch.
const defaultRetrySettings = {
    // maximum time to sleep
    maxSleep: 100,
    // minimum time to sleep before retrying a request
    minSleep: 10,
    // a hook that is called on every retry, default to logging.
    // users can pass their own onRetry hook, which receives a reference to the
    // active request; this can be aborted with a reference to the request id
    onRetry: message => window.mwc.logger.warn('mwcDataAccess', message),
};

// the named extensions that are supported by this interface
const extensions = ['url', 'source', 'timeout', ...Object.keys(defaultRetrySettings)];
// status messages for requests
const REQUEST_ABORTED = 'request aborted';
const REQUEST_PENDING = 'request is pending';
const REQUEST_RETRIED = 'request was retried';
const REQUEST_TIMEOUT = 'request timed out';
// for tokens
const TOKEN_AVAILABLE = 'token is available';
const TOKEN_REHYDRATE = 'awaiting token refresh';
const TOKEN_UNDEFINED = 'token is uninitialized';

// the methods have privileged access to activeRequests, sourceTypes, and tokens
function manifest({ activeRequests, sourceTypes, tokens }) {
    const methods = {
        abort(request = {}) {
            if (activeRequests.has(request.id)) {
                activeRequests.get(request.id).status = REQUEST_ABORTED;
            } else {
                throw new Error('the promise cannot be aborted');
            }
        },
        fetchInternal() {
            // did you forget to polyfill?
            if (window.fetch === undefined) {
                throw new Error('window.fetch is undefined');
            }
            return window.fetch;
        },
        getRequest(options, requestId) {
            const { url, source = 'default' } = options;
            const { decorateRequest, validTokenForRequest } = sourceTypes.get(source);
            // get a reference to the active request
            const tracking = activeRequests.get(requestId);
            // copy the options object instead of mutating
            const requestOptions = Object.assign({}, options);
            // options.maxAttempts must be set to trigger retry logic
            if (options.maxAttempts > 0) {
                // apply defaults for min/max sleep and the onRetry hook;
                // this does mutate the options object passed here
                options = Object.assign({}, defaultRetrySettings, options);
            }
            // sanitize the options passed to native fetch
            extensions.forEach(key => {
                delete requestOptions[key];
            });
            // getToken takes a callback, which may be pooled awaiting a token refresh
            return new Promise((resolve, reject) => {
                methods.getToken(source, (error, token) => {
                    // the application has to handle errors getting tokens
                    if (error) {
                        return reject(error);
                    }
                    if (!validTokenForRequest(token)) {
                        methods.rehydrate(source);
                        // this condition may be reached before the request is active
                        if (tracking) {
                            tracking.status = REQUEST_RETRIED;
                        }
                        // the getRequest method will call itself until validTokenForRequest returns true.
                        // this does introduce the potential for an infinite loop.
                        // one way to circumvent this will be to introduce some throttling or maximum retries
                        // in the refreshToken or validTokenForRequest callbacks.
                        return resolve(methods.getRequest.call(this, options, requestId));
                    }
                    // the client determines how tokens are attached to requests
                    return resolve([
                        url,
                        decorateRequest({
                            token,
                            requestOptions,
                        }),
                    ]);
                });
            });
        },
        getToken(source, callback) {
            const token = tokens.get(source);
            if (token.status === TOKEN_AVAILABLE) {
                // doesn't know or care if the token is valid
                callback(null, token.value);
            } else if (token.status === TOKEN_REHYDRATE) {
                // await the pending refresh
                token.pool.add(callback);
            } else {
                // this will surface error messages from refreshToken
                callback(token.value);
            }
        },
        identifyRequest(requestOptions) {
            // the request may not have defined headers
            const { headers = {} } = requestOptions;
            const key = 'X-API-RequestId';
            // header keys are case-insensitive
            const phrase = new RegExp(key, 'i');
            // don't overwrite an existing request id
            if (
                !Object.keys(headers)
                    .toString()
                    .match(phrase)
            ) {
                headers[key] = uuid();
                requestOptions.headers = headers;
            }
            return requestOptions;
        },
        registerSource(type, defaultOverride = {}) {
            // don't force people to make hard decisions like naming things
            const source = type.name || uuid();
            const hooks = {};
            // check for collisions
            if (sourceTypes.has(source) || tokens.has(source)) {
                window.mwc.logger.warn('mwcDataAccess', `a token source named '${source}' is already registered`);
                return source;
            }
            // set the initial status for each source/token
            tokens.set(source, {
                pool: new Set(),
                status: type.token ? TOKEN_AVAILABLE : TOKEN_UNDEFINED,
                value: type.token || null,
            });
            // define the callback hooks, subbing in defaults
            ['decorateRequest', 'refreshToken', 'responseSignalsTokenRefresh', 'validTokenForRequest'].forEach(fn => {
                // get a reference to the callback
                const callback = type[fn] || defaultOverride[fn] || defaultSource[fn];
                // make sure these are functions
                if (callback && typeof callback !== 'function') {
                    throw new Error(`'${source}.${fn}' must be a function`);
                }
                // apply defaults
                hooks[fn] = callback;
            });
            // throw an error if a refreshToken callback is not defined
            if (!hooks.refreshToken && source !== 'default') {
                throw new Error(`a refreshToken callback is not defined for source type "${source}"`);
            }
            // associate the callback hooks with the source type
            sourceTypes.set(source, hooks);
            // make the initial call to get the token
            if (hooks.refreshToken && !type.token) {
                methods.rehydrate(source);
            }
            // this is necessary if the source name was auto-generated
            return source;
        },
        rehydrate(source) {
            const token = tokens.get(source);
            // signal that a token refresh is pending
            token.status = TOKEN_REHYDRATE;
            sourceTypes.get(source).refreshToken(
                (error, data) => {
                    // update the token
                    token.status = error ? TOKEN_UNDEFINED : TOKEN_AVAILABLE;
                    token.value = error || data;
                    // invoke and drain all pending callbacks
                    token.pool.forEach(callback => {
                        token.pool.delete(callback);
                        callback(error, error ? null : data);
                    });
                },
                { token: source }
            );
        },
        retry(options, requestId) {
            const { minSleep, maxSleep, maxAttempts } = options;
            // get the active request
            const tracking = activeRequests.get(requestId);
            // what attempt is this?
            const attempt = maxAttempts - tracking.attempts;
            // calculate time for exponential backoff with jitter
            const interval = minSleep || tracking.interval;
            // the value returned is a random number in the range from minSleep to maxSleep
            // see https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter
            tracking.interval = Math.min(maxSleep, interval + Math.random() * (2 ** attempt));
            // decrement the number of attempts remaining
            tracking.attempts -= 1;
            // signal that this request is being retried
            tracking.status = REQUEST_RETRIED;

            return new Promise(resolve => {
                window.setTimeout(() => {
                    // make a new request reusing the existing request id
                    const recycled = this.fetch(options, requestId);

                    // invoke the retry hook - let the client application respond
                    if (options.onRetry && typeof options.onRetry === 'function') {
                        options.onRetry(
                            `request to '${options.url}' failed - ${tracking.attempts} attempts remaining; slept ${interval}ms.`
                        );
                    }

                    // the backoff/jitter helps resolve spikes in traffic when a service needs to scale up
                    resolve(recycled);
                }, interval);
            });
        },
        send(options, id) {
            const { source = 'default' } = options;
            const sourceType = sourceTypes.get(source);
            if (!sourceType) {
                throw new Error(`a token source named '${source}' has not been registered`);
            }

            const { responseSignalsTokenRefresh } = sourceType;
            const fetch = methods.fetchInternal();

            // grab a unique id, or retry using an active request id
            const requestId =
                id && activeRequests.has(id) && activeRequests.get(id).status === REQUEST_RETRIED ? id : uuid();

            // get the validated and/or decorated request object.
            // errors getting the token from the rehydrate callback need to be handled by the application.
            const req = methods.getRequest
                .call(this, options, requestId)
                .then(args => fetch(...args))
                .then(response => {
                    // get a reference to the active request
                    const tracking = activeRequests.get(requestId);
                    // get the json content prior to sending the response object to any callbacks
                    const clone = response.clone();
                    // if the request is not aborted or timed out
                    if (tracking.status === REQUEST_PENDING) {
                        // invoke the callback to determine if a token refresh is required
                        // the retry logic should be invoked first
                        if (String(response.status).match(/^[45]/) && tracking.attempts > 0) {
                            // drop the response, and retry the request
                            return methods.retry.call(this, options, requestId);
                            // send a clone to the callback where it can be manipulated without mutating the response
                        } else if (responseSignalsTokenRefresh(clone)) {
                            // if there are no more attempts remaining, or no retry logic,
                            // determine if the response warrants a token refresh
                            methods.rehydrate.call(this, source);
                            tracking.status = REQUEST_RETRIED;
                            return this.fetch(options, requestId);
                        }
                        // clear the timeout, if it exists
                        window.clearTimeout(tracking.timer);
                        // remove the request id from the set of active requests
                        activeRequests.delete(requestId);
                        // proceed as usual
                        return response;
                    }
                    // should this be an error?
                    return Promise.reject(new Error(tracking.status));
                });

            // update active requests
            if (activeRequests.has(requestId)) {
                activeRequests.get(requestId).status = REQUEST_PENDING;
            } else {
                // track new requests
                activeRequests.set(requestId, {
                    attempts: options.maxAttempts || 0,
                    status: REQUEST_PENDING,
                });
            }

            if (options.timeout) {
                // this can be combined with retry / automatic token-refresh
                methods.setTimer(options.timeout, requestId);
            }

            // return the fetch request amended with a (read-only) id prop
            return Object.defineProperty(req, 'id', {
                value: requestId,
            });
        },
        setTimer(time, requestId) {
            const tracking = activeRequests.get(requestId);
            tracking.timer = window.setTimeout(() => {
                tracking.status = REQUEST_TIMEOUT;
            }, time);
        },
    };
    // return a copy of the function manifest
    return methods;
}

// return the class contstructor
export default class RequestController {
    /*
        sources is an array where each object provides the following parameters
        and optional callbacks for token management. if a callback is not
        included, the application falls back on a set of default behaviors.

        name
        - required
        - must be unique among the collection of elements passed as 'sources'

        decorateRequest
        - is passed a token and an options object
        - must return a well-formed options object for a fetch request

        refreshToken
        - required for each source, or provided for a custom 'default' source
        - expected to be asynchronous
        - follows the standard NodeJS callback pattern
        - invokes a callback with (error, token)

        validTokenForRequest
        - will validate the token before decorateRequest is called
        - is passed the token
        - returns a boolean

        responseSignalsTokenRefresh
        - check the response to determine if a token refresh is needed
        - is passed the whole response object
        - returns a boolean
    */
    constructor(options = {}) {
        // don't error if a sources array is not included
        const { sources = [] } = options;
        // a map for tracking the status of active requests by uuid
        const activeRequests = new Map();
        // a map of source names to token source callbacks
        const sourceTypes = new Map();
        // a map of source names to token values
        const tokens = new Map();

        // get a copy of the function manifest
        const methods = manifest({
            activeRequests,
            sourceTypes,
            tokens,
        });

        // returns a set of source names, or false if there are any missing names or duplicate names
        const sourceNames = sources.reduce((names, source) => {
            if (names && source.name && !names.has(source.name)) {
                names.add(source.name);
                return names;
            }
            return false;
        }, new Set());

        // a placeholder for referencing the default source
        let defaultOverride;

        // throw an error if elements of sources are duplicative or malformed
        if (!sourceNames) {
            throw new Error('each source must be named. the collection must not have duplicative elements');
        }

        // amend sources with a default (if none is provided)
        // and update our reference to the default source
        if (!sourceNames.has('default')) {
            defaultOverride = defaultSource;
            sources.push(defaultSource);
        } else {
            defaultOverride = sources.find(s => s.name === 'default');
        }

        // establish callbacks for each token source
        sources.forEach(type => methods.registerSource(type, defaultOverride));

        //  define the abort and fetch methods here, so they have access to the private methods object
        Object.defineProperties(this, {
            abort: {
                value: request => {
                    return methods.abort.call(this, request);
                },
                writable: true,
            },
            fetch: {
                value: (params = {}, requestId = null) => {
                    return methods.send.call(this, params, requestId);
                },
                writable: true,
            },
            registerTokenSource: {
                value: (params = {}) => {
                    return methods.registerSource.call(this, params);
                },
                writable: true,
            },
        });
    }
}
